var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import Table from "@cloudscape-design/components/table";
import { useCollection } from "@cloudscape-design/collection-hooks";
import Pagination from "@cloudscape-design/components/pagination";
import CollectionPreferences from "@cloudscape-design/components/collection-preferences";
import TextFilter from "@cloudscape-design/components/text-filter";
import { useDrop } from "@react-aria/dnd";
import { useFileExplorerStore } from "../store";
import { useFiles, useTablePreferences } from "../hooks";
import { TableHeader } from "./table-header";
import { DropZone, NoItemsEmptyState, NoMatchEmptyState, } from "./empty-table-content";
import { QueryClient, QueryClientProvider } from "react-query";
import defaults from "lodash/defaults";
import { getColumnDefinitions } from "./column-definitions";
import { defaultAllowedActions, defaultPageSizes, defaultTableVariant, } from "../constants";
import "../FileExplorer.css";
var queryClient = new QueryClient();
/**
 * File explorer component
 * This is the main exported component which is responsible for rendering the file explorer
 * Props documentation can be found in the IFileExplorerProps interface
 */
function FileExplorer(props) {
    var _a;
    var _b = props.showActions, showActions = _b === void 0 ? true : _b, i18nStrings = props.i18nStrings, useCollectionOptions = props.useCollectionOptions, userPaginationProps = props.paginationProps, textFilterProps = props.textFilterProps, tableVariant = props.tableVariant, collectionPreferencesProps = props.collectionPreferencesProps, AdditionalHeaderElement = props.AdditionalHeaderElement, _c = props.pageSizes, pageSizes = _c === void 0 ? defaultPageSizes : _c, allowedActionsFromProps = props.allowedActions, _d = props.contentDensity, contentDensity = _d === void 0 ? "comfortable" : _d, rootPath = props.rootPath, minContainerHeightRequested = props.minContainerHeight;
    var allowedActions = defaults(allowedActionsFromProps, defaultAllowedActions);
    var _e = useFiles(props), actions = _e.actions, selectedFiles = _e.selectedFiles, files = _e.files, isFetching = _e.isFetching, isLoading = _e.isLoading, refetch = _e.refetch;
    var _f = useTablePreferences(pageSizes), preferences = _f.preferences, setPreferences = _f.setPreferences;
    var isRenaming = useFileExplorerStore(function (s) {
        return Boolean(s.renaming.initialFileName);
    });
    var isCreatingFolder = useFileExplorerStore(function (s) { return s.newFolderName !== null; });
    var isUploading = useFileExplorerStore(function (s) { return Boolean(s.uploading.files); });
    var path = useFileExplorerStore(function (s) { return s.path; });
    var columnDefinitions = getColumnDefinitions(allowedActions, actions, i18nStrings, (_a = useCollectionOptions === null || useCollectionOptions === void 0 ? void 0 : useCollectionOptions.sorting) === null || _a === void 0 ? void 0 : _a.groupByType);
    var pageSizePreference = React.useMemo(function () {
        return {
            title: i18nStrings.selectPageSize,
            options: pageSizes.map(function (size) { return ({
                value: size,
                label: "".concat(size.toLocaleString(), " ").concat(size === 1 ? i18nStrings.entry : i18nStrings.entries),
            }); }),
        };
    }, [pageSizes, i18nStrings]);
    var _g = React.useState(false), isDragging = _g[0], setIsDragging = _g[1];
    var _h = useCollection(isDragging ? [] : files, {
        filtering: __assign({ empty: isDragging ? (_jsx(DropZone, { i18nStrings: i18nStrings })) : (_jsx(NoItemsEmptyState, { i18nStrings: i18nStrings, allowedActions: allowedActions })), noMatch: (_jsx(NoMatchEmptyState, { clearFilter: function () {
                    collectionActions.setFiltering("");
                }, i18nStrings: i18nStrings })) }, useCollectionOptions === null || useCollectionOptions === void 0 ? void 0 : useCollectionOptions.filtering),
        pagination: __assign({ pageSize: preferences.pageSize }, useCollectionOptions === null || useCollectionOptions === void 0 ? void 0 : useCollectionOptions.pagination),
        sorting: __assign({}, useCollectionOptions === null || useCollectionOptions === void 0 ? void 0 : useCollectionOptions.sorting),
        selection: __assign({}, useCollectionOptions === null || useCollectionOptions === void 0 ? void 0 : useCollectionOptions.selection),
        propertyFiltering: (useCollectionOptions === null || useCollectionOptions === void 0 ? void 0 : useCollectionOptions.propertyFiltering)
            ? __assign({}, useCollectionOptions === null || useCollectionOptions === void 0 ? void 0 : useCollectionOptions.propertyFiltering) : undefined,
    }), items = _h.items, collectionActions = _h.actions, collectionProps = _h.collectionProps, filterProps = _h.filterProps, paginationProps = _h.paginationProps;
    var ref = React.useRef(null);
    var dropProps = useDrop({
        ref: ref,
        onDropEnter: function () {
            setIsDragging(true);
        },
        onDropExit: function () {
            setIsDragging(false);
        },
    }).dropProps;
    // When creating a folder, reset the sorting and take the user to the first page to see the new folder input
    React.useEffect(function () {
        if (!isCreatingFolder) {
            return;
        }
        collectionActions.setSorting({
            sortingColumn: {},
            isDescending: undefined,
        });
        collectionActions.setCurrentPage(1);
    }, [isCreatingFolder]);
    // When the path changes make sure the user lands on the first page and clear the filtering text
    React.useEffect(function () {
        collectionActions.setCurrentPage(1);
        collectionActions.setFiltering("");
    }, [path]);
    // Handle un-mount
    React.useEffect(function () {
        return function () {
            // When the component unmounts, we reset renaming state and clear the draft folder
            actions.stopCreatingFolder();
            actions.stopRenaming();
        };
    }, []);
    var dropPropsToUse = (allowedActions === null || allowedActions === void 0 ? void 0 : allowedActions.upload) && !isUploading ? dropProps : {};
    var minContainerHeight = minContainerHeightRequested !== null && minContainerHeightRequested !== void 0 ? minContainerHeightRequested : null;
    return (_jsxs("div", __assign({}, dropPropsToUse, { ref: ref, "data-testid": "dropzone", onDrop: function (ev) {
            if (!(allowedActions === null || allowedActions === void 0 ? void 0 : allowedActions.upload)) {
                return;
            }
            setIsDragging(false);
            void actions.onDrop(ev);
        }, className: "file-explorer-table-container", style: minContainerHeight !== null
            ? {
                minHeight: minContainerHeight,
            }
            : {} }, { children: [_jsx(Table, __assign({}, collectionProps, { contentDensity: contentDensity, loading: isLoading, selectionType: showActions ? "multi" : undefined, header: _jsx(TableHeader, __assign({ actions: actions, isFetching: isFetching, i18nStrings: i18nStrings, showActions: showActions, allowedActions: allowedActions, rootPath: rootPath }, { children: AdditionalHeaderElement && (_jsx(AdditionalHeaderElement, { refetch: refetch })) })), columnDefinitions: columnDefinitions, selectedItems: selectedFiles, onSelectionChange: function (ev) {
                    var _a;
                    useFileExplorerStore.setState({
                        selectedFiles: (_a = ev.detail.selectedItems) !== null && _a !== void 0 ? _a : [],
                    });
                }, resizableColumns: true, items: items, pagination: _jsx(Pagination, __assign({}, paginationProps, userPaginationProps, { ariaLabels: i18nStrings.paginationAriaLabels })), filter: _jsx(TextFilter, __assign({}, filterProps, { filteringAriaLabel: i18nStrings.filteringAriaLabel, filteringClearAriaLabel: i18nStrings.filteringClearAriaLabel, countText: "".concat(items.length, " ").concat(items.length === 1 ? i18nStrings.entry : i18nStrings.entries) }, textFilterProps)), preferences: _jsx(CollectionPreferences, __assign({ onConfirm: function (_a) {
                        var detail = _a.detail;
                        setPreferences(detail);
                    }, preferences: preferences, pageSizePreference: pageSizePreference, wrapLinesPreference: {
                        label: i18nStrings.wrapLinesLabel,
                        description: i18nStrings.wrapLinesDescription,
                    }, cancelLabel: i18nStrings.cancelLabel, confirmLabel: i18nStrings.confirmLabel, title: i18nStrings.preferencesTitle }, collectionPreferencesProps)), variant: tableVariant !== null && tableVariant !== void 0 ? tableVariant : defaultTableVariant, ariaLabels: i18nStrings.tableAriaLabels, sortingDisabled: isRenaming || isCreatingFolder, wrapLines: preferences.wrapLines, isItemDisabled: function (item) { return Boolean(item.isDraft || item.isRenaming); } })), _jsx("div", __assign({ className: "screen-reader-only", role: "status" }, { children: !isFetching ? i18nStrings.contentRefreshedAriaLabel : "" }))] })));
}
export function FileExplorerWrapper(props) {
    return (_jsx(QueryClientProvider, __assign({ client: queryClient }, { children: _jsx(FileExplorer, __assign({}, props)) })));
}
export default FileExplorerWrapper;
